import { Controller } from "@hotwired/stimulus"

// Several files of a list chosen at once (Malware scan, 08.10.2026): a click
// anywhere on a row selects it -- no checkbox --, and a bar with the actions
// for the selection appears above the list. Clicks on a row's own buttons,
// links and folded details do what they always did, and marking text with the
// mouse does not select.
//
// The bar offers the same actions as a file's own menu, in the same order
// (09.10.2026): each says for how many of the chosen files it applies; what
// applies to none, or only makes sense for one file at a time, is greyed out
// with the reason.
//
//   data-controller="selection"
//   row:    data-selection-target="row" data-path="…" data-actions="view copy_path quarantine"
//           data-action="click->selection#toggle keydown->selection#key"
//   bar:    a form, data-selection-target="bar", always there ("Select all visible", 19.10.2026)
//   chosen: what only shows once something is chosen (count, "Clear selection", the actions menu)
//   hint:   what shows while nothing is chosen (how to select)
//   item:   a menu entry, data-selection-target="item" data-key="quarantine" data-action="selection#run",
//           with [data-role=count] and [data-role=reason] inside
//   submit: a hidden submit button per action that changes something (data-key), carrying the confirmation
//   several: whether actions that change the site may run for several files at once (the plan)
//   everything: every file of the filtered list as [path, "actions"] (19.10.2026) -- "Select all"
//           means all of them, on every page, not only the rows on screen
export default class extends Controller {
  static targets = ["row", "bar", "count", "inputs", "item", "submit", "chosen", "hint"]
  static values = { countText: String, several: Boolean, reasonNone: String, reasonSingle: String, reasonPaid: String, copied: String, everything: Array }

  // One file at a time: they open a page or hand out one file.
  static SINGLE = ["view", "compare", "download_original", "download_file"]
  // They change the site: for several files at once only where the plan allows it.
  static CHANGING = ["replace", "restore", "quarantine", "delete"]

  connect() {
    this.whole = false
    this.update()
  }

  toggle(event) {
    if (event.target.closest("a, button, form, input, select, textarea, summary, label, .dropdown-menu")) return
    if (window.getSelection && String(window.getSelection()).length > 0) return
    this.flip(event.currentTarget)
  }

  key(event) {
    if (event.target !== event.currentTarget) return
    if (event.key !== " " && event.key !== "Enter") return
    event.preventDefault()
    this.flip(event.currentTarget)
  }

  clear() {
    this.whole = false
    this.rowTargets.forEach((row) => this.mark(row, false))
    this.update()
  }

  // Every file of the filtered list, also those on other pages or folded away.
  all() {
    this.whole = this.everythingValue.length > 0
    this.rowTargets.forEach((row) => this.mark(row, true))
    this.update()
  }

  flip(row) {
    // Taking one file out of "all" leaves exactly the rows marked on this page.
    if (this.whole) this.whole = false
    this.mark(row, !row.classList.contains("is-selected"))
    this.update()
  }

  mark(row, on) {
    row.classList.toggle("is-selected", on)
    row.setAttribute("aria-checked", on ? "true" : "false")
  }

  // The chosen files as { path, actions, row } -- the whole list after "Select all".
  get chosen() {
    if (this.whole) {
      const rows = new Map(this.rowTargets.map((row) => [row.dataset.path, row]))
      return this.everythingValue.map(([path, actions]) => ({ path, actions: actions.split(" "), row: rows.get(path) }))
    }
    return this.rowTargets.filter((row) => row.classList.contains("is-selected"))
      .map((row) => ({ path: row.dataset.path, actions: (row.dataset.actions || "").split(" "), row }))
  }

  // The chosen files an action applies to.
  rowsFor(key) { return this.chosen.filter((file) => file.actions.includes(key)) }

  // { count, reason } -- reason is null when the action can run for the selection.
  state(key) {
    const count = this.rowsFor(key).length
    const several = this.chosen.length > 1
    if (count === 0) return { count, reason: this.reasonNoneValue }
    if (several && this.constructor.SINGLE.includes(key)) return { count, reason: this.reasonSingleValue }
    if (several && this.constructor.CHANGING.includes(key) && !this.severalValue) return { count, reason: this.reasonPaidValue }
    return { count, reason: null }
  }

  update() {
    if (!this.hasBarTarget) return
    const chosen = this.chosen
    this.chosenTargets.forEach((element) => { element.hidden = chosen.length === 0 })
    this.hintTargets.forEach((element) => { element.hidden = chosen.length > 0 })
    if (this.hasCountTarget) this.countTarget.textContent = this.countTextValue.replace("%{count}", chosen.length)
    this.itemTargets.forEach((item) => {
      const { count, reason } = this.state(item.dataset.key)
      item.classList.toggle("disabled", reason !== null)
      item.setAttribute("aria-disabled", reason !== null ? "true" : "false")
      const number = item.querySelector("[data-role=count]")
      if (number) number.textContent = `(${count})`
      const why = item.querySelector("[data-role=reason]")
      if (why) { why.textContent = reason || ""; why.hidden = reason === null }
    })
  }

  // The chosen action for the selection.
  async run(event) {
    const item = event.currentTarget
    const key = item.dataset.key
    if (this.state(key).reason !== null) return
    const rows = this.rowsFor(key)

    // One file: exactly what its own menu does.
    if (this.chosen.length === 1 && rows[0].row) {
      rows[0].row.querySelector(`.repair-file-menu [data-action-key="${key}"]`)?.click()
      return
    }
    if (key === "copy_path") {
      await this.copy(rows.map((file) => file.path).join("\n"), item)
      return
    }
    const submit = this.submitTargets.find((button) => button.dataset.key === key)
    if (!submit) return
    this.inputsTarget.replaceChildren(...rows.map((file) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "paths[]"
      input.value = file.path
      return input
    }))
    this.barTarget.requestSubmit(submit)
  }

  async copy(text, item) {
    try {
      await navigator.clipboard.writeText(text)
    } catch (_) {
      const area = document.createElement("textarea")
      area.value = text
      area.style.position = "fixed"
      area.style.opacity = "0"
      document.body.appendChild(area)
      area.select()
      document.execCommand("copy")
      area.remove()
    }
    const number = item.querySelector("[data-role=count]")
    if (!number) return
    const before = number.textContent
    number.textContent = `– ${this.copiedValue}`
    setTimeout(() => { number.textContent = before }, 1500)
  }
}
